import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useGitStore } from "../../stores/useGitStore";
import {
  describeFacets,
  hasFacets,
  matchesFacets,
  parseQuery,
  type FacetTarget,
} from "../../lib/palette-filter";

const pages = [
  { label: "dashboard", to: "/" },
  { label: "templates", to: "/templates" },
  { label: "snippets", to: "/snippets" },
  { label: "live", to: "/live" },
  { label: "sell sheets", to: "/sell-sheets" },
  { label: "audit", to: "/audit" },
  { label: "settings", to: "/settings" },
] as const;

interface PaletteItem {
  key: string;
  label: string;
  to: string;
}

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

export function CommandPalette({ open, onClose }: CommandPaletteProps) {
  const navigate = useNavigate();
  const docs = useGitStore((s) => s.docs);
  const fetchTree = useGitStore((s) => s.fetchTree);
  const [query, setQuery] = useState("");
  const [sel, setSel] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      setQuery("");
      setSel(0);
      fetchTree();
      const t = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
  }, [open, fetchTree]);

  const { facets, freeText } = useMemo(() => parseQuery(query), [query]);
  const facetsActive = hasFacets(facets);
  const facetHint = facetsActive ? describeFacets(facets) : "";

  const { pageItems, docItems } = useMemo(() => {
    const q = freeText.trim().toLowerCase();

    const pageItems: PaletteItem[] = pages
      .filter((p) => !q || p.label.includes(q))
      .map((p) => ({ key: `page:${p.to}`, label: p.label, to: p.to }));

    // Keep the latest doc per family (last one wins, matching prior behaviour)
    // along with the facet fields needed to filter it.
    const families = new Map<
      string,
      PaletteItem & { facet: FacetTarget; family: string; docType: string }
    >();
    for (const doc of docs) {
      const item = {
        key: `family:${doc.family}`,
        label: doc.title,
        to: `/family/${encodeURIComponent(doc.family)}`,
        facet: {
          status: doc.status,
          docType: doc.docType,
          nextReview: doc.nextReview,
        },
        family: doc.family,
        docType: doc.docType,
      };
      families.set(doc.family, item);
    }
    const docItems = [...families.values()]
      .filter((d) => matchesFacets(d.facet, facets))
      .filter((d) => {
        if (!q) return true;
        const hay = `${d.label} ${d.docType} ${d.family}`.toLowerCase();
        return hay.includes(q);
      })
      .map(
        ({ facet: _facet, family: _family, docType: _docType, ...item }) =>
          item,
      )
      .slice(0, q || facetsActive ? 20 : 8);

    return { pageItems, docItems };
  }, [freeText, facets, facetsActive, docs]);

  const flat = useMemo(
    () => [...pageItems, ...docItems],
    [pageItems, docItems],
  );

  useEffect(() => {
    setSel(0);
  }, [query]);

  useEffect(() => {
    listRef.current
      ?.querySelector('[data-selected="true"]')
      ?.scrollIntoView({ block: "nearest" });
  }, [sel]);

  if (!open) return null;

  const go = (item: PaletteItem) => {
    navigate(item.to);
    onClose();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSel((s) => (flat.length ? (s + 1) % flat.length : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSel((s) => (flat.length ? (s - 1 + flat.length) % flat.length : 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (flat[sel]) go(flat[sel]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    }
  };

  const renderSection = (
    label: string,
    items: PaletteItem[],
    offset: number,
  ) => {
    if (items.length === 0) return null;
    return (
      <div>
        <div style={styles.sectionLabel}>{label}</div>
        {items.map((item, i) => {
          const idx = offset + i;
          const selected = idx === sel;
          return (
            <div
              key={item.key}
              data-selected={selected || undefined}
              style={{ ...styles.row, ...(selected ? styles.rowSelected : {}) }}
              onMouseEnter={() => setSel(idx)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => go(item)}
            >
              <span style={styles.rowLabel}>{item.label}</span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.card} onClick={(e) => e.stopPropagation()}>
        <div style={styles.inputRow}>
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={styles.inputIcon}
          >
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.5-3.5" />
          </svg>
          <input
            ref={inputRef}
            className="palette-input"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="search hub & repo"
            style={styles.input}
          />
          <span style={styles.escHint}>esc</span>
        </div>
        {facetHint && <div style={styles.facetHint}>{facetHint}</div>}
        <div ref={listRef} style={styles.list}>
          {flat.length === 0 ? (
            <div style={styles.empty}>no results for "{query}"</div>
          ) : (
            <>
              {renderSection("pages", pageItems, 0)}
              {renderSection("documents", docItems, pageItems.length)}
            </>
          )}
        </div>
        <div style={styles.footer}>↑↓ navigate · ↵ open · esc close</div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0, 0, 0, 0.35)",
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    paddingTop: "16vh",
    zIndex: 1000,
  },
  card: {
    width: 560,
    maxWidth: "calc(100vw - 48px)",
    background: "var(--comp-paper)",
    border: "1px solid var(--comp-rule)",
    borderRadius: "var(--radius-lg)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  inputRow: {
    display: "flex",
    alignItems: "center",
    gap: "var(--space-2)",
    padding: "10px var(--space-3)",
    borderBottom: "1px solid var(--comp-rule)",
  },
  inputIcon: {
    color: "var(--fg-3)",
    flexShrink: 0,
  },
  input: {
    flex: 1,
    border: "none",
    background: "transparent",
    fontFamily: "var(--font-mono)",
    fontSize: 13,
    color: "var(--fg-1)",
    outline: "none",
  },
  escHint: {
    fontSize: 10,
    color: "var(--fg-mute)",
    fontFamily: "var(--font-mono)",
  },
  facetHint: {
    padding: "5px var(--space-3)",
    fontSize: 10,
    color: "var(--fg-mute)",
    fontFamily: "var(--font-mono)",
    borderBottom: "1px solid var(--comp-rule)",
  },
  list: {
    maxHeight: 320,
    overflowY: "auto",
    padding: "4px 0",
  },
  sectionLabel: {
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: "0.08em",
    color: "var(--fg-mute)",
    fontFamily: "var(--font-mono)",
    padding: "6px var(--space-3) 2px",
  },
  row: {
    display: "flex",
    alignItems: "baseline",
    gap: "var(--space-2)",
    padding: "5px var(--space-3)",
    fontFamily: "var(--font-mono)",
    fontSize: 12,
    cursor: "pointer",
  },
  rowSelected: {
    background: "var(--comp-hover)",
  },
  rowLabel: {
    color: "var(--fg-1)",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  empty: {
    padding: "16px var(--space-3)",
    fontSize: 12,
    color: "var(--fg-3)",
    fontFamily: "var(--font-mono)",
  },
  footer: {
    borderTop: "1px solid var(--comp-rule)",
    padding: "6px var(--space-3)",
    fontSize: 10,
    color: "var(--fg-mute)",
    fontFamily: "var(--font-mono)",
  },
};
